'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import GameCard from './GameCard';
import CategoryChips from './CategoryChips';
import FeaturedBanner from './FeaturedBanner';
import EmptyState from '@/components/ui/EmptyState';
import { createClient } from '@/lib/supabase/client';

interface Game {
  id: string;
  title: string;
  category: string;
  plays_count: number;
  likes_count: number;
  thumbnail_url: string | null;
  is_featured: boolean;
  created_at: string;
  user_profiles: { username: string; display_name: string } | null;
  ai_models: { badge: string } | null;
}

const SORT_OPTIONS = [
  { id: 'sort-popular', label: 'Mais populares' },
  { id: 'sort-recent', label: 'Mais recentes' },
  { id: 'sort-played', label: 'Mais jogados' },
];

const ITEMS_PER_PAGE = 9;

export default function ExploreScreen() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [selectedSort, setSelectedSort] = useState('sort-popular');
  const [selectedModel, setSelectedModel] = useState('Todos');
  const [page, setPage] = useState(1);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      try {
        const supabase = createClient();
        let query = supabase
          .from('games')
          .select('id, title, category, plays_count, likes_count, thumbnail_url, is_featured, created_at, user_profiles(username, display_name), ai_models(badge)', { count: 'exact' })
          .eq('game_status', 'published');

        if (selectedCategory !== 'Todos') {
          query = query.eq('category', selectedCategory);
        }
        if (search) {
          query = query.ilike('title', `%${search}%`);
        }
        if (selectedModel !== 'Todos') {
          query = query.eq('ai_models.badge', selectedModel.toLowerCase());
        }

        if (selectedSort === 'sort-popular') {
          query = query.order('likes_count', { ascending: false });
        } else if (selectedSort === 'sort-played') {
          query = query.order('plays_count', { ascending: false });
        } else {
          query = query.order('created_at', { ascending: false });
        }

        const from = (page - 1) * ITEMS_PER_PAGE;
        const to = from + ITEMS_PER_PAGE - 1;
        query = query.range(from, to);

        const { data, count } = await query;
        setGames((data as Game[]) || []);
        setTotalCount(count || 0);
      } catch {}
      setLoading(false);
    };
    fetchGames();
  }, [search, selectedCategory, selectedSort, selectedModel, page]);

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const featuredGame = games.find((g) => g.is_featured) ?? games[0];

  const mapGameForCard = (g: Game) => ({
    id: g.id,
    title: g.title,
    category: g.category,
    plays: g.plays_count,
    likes: g.likes_count,
    author: g.user_profiles?.username || 'anon',
    authorAvatar: (g.user_profiles?.display_name || g.user_profiles?.username || 'A').slice(0, 2).toUpperCase(),
    thumbnail: g.thumbnail_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1ec9bad5d-1766953217710.png',
    thumbnailAlt: `${g.title} game thumbnail`,
    model: (g.ai_models?.badge === 'advanced' ? 'Advanced' : g.ai_models?.badge === 'pro' ? 'Pro' : 'Free') as 'Free' | 'Pro' | 'Advanced',
    trending: g.likes_count > 200,
    createdAt: new Date(g.created_at).toLocaleDateString('pt-PT'),
  });

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Explorar jogos</h1>
        <p className="text-sm text-muted-foreground">Descobre jogos criados pela comunidade GameForge</p>
      </div>

      {featuredGame && !loading && (
        <FeaturedBanner game={mapGameForCard(featuredGame)} />
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Pesquisar jogos, autores..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          />
        </div>

        <select
          value={selectedSort}
          onChange={(e) => { setSelectedSort(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>

        <select
          value={selectedModel}
          onChange={(e) => { setSelectedModel(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        >
          {['Todos', 'Free', 'Pro', 'Advanced'].map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      <CategoryChips
        selected={selectedCategory}
        onSelect={(cat) => { setSelectedCategory(cat); setPage(1); }}
      />

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="rounded-2xl bg-card border border-border overflow-hidden animate-pulse">
              <div className="aspect-video bg-muted" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : games.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon="MagnifyingGlassIcon"
            title="Nenhum jogo encontrado"
            description="Tenta ajustar os filtros ou pesquisar por outro termo."
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {games.map((game) => (
              <GameCard key={game.id} game={mapGameForCard(game)} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-card border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Anterior
              </button>
              <span className="text-sm text-muted-foreground px-2">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-card border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
